import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConversationArea = ({
  conversation,
  messages,
  onSendMessage,
  isTyping,
  onToggleParticipantPanel
}) => {
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = () => {
    if (messageInput?.trim()) {
      onSendMessage(messageInput);
      setMessageInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <Icon name="MessageCircle" size={64} className="text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">Select a conversation</h3>
        <p className="text-muted-foreground">Choose a conversation from the list to start messaging</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={conversation?.avatar}
              alt={conversation?.avatarAlt}
              className="w-10 h-10 rounded-full object-cover"
            />
            {conversation?.isOnline && (
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-success border-2 border-card rounded-full" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{conversation?.name}</h3>
            <p className="text-xs text-muted-foreground">
              {conversation?.isOnline ? 'Online' : `Last active ${conversation?.lastMessageTime}`}
              {conversation?.type === 'group' && ` • ${conversation?.participants} participants`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" iconName="Phone" />
          <Button variant="ghost" size="icon" iconName="Video" />
          <Button variant="ghost" size="icon" iconName="MoreVertical" />
          <Button
            variant="ghost"
            size="icon"
            iconName="PanelRightOpen"
            onClick={onToggleParticipantPanel}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.map((message, index) => {
          const isMe = message?.senderId === 'me';
          const showAvatar = !isMe && (index === 0 || messages?.[index - 1]?.senderId !== message?.senderId);

          return (
            <div
              key={message?.id}
              className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {!isMe && (
                <div className="w-8 h-8 flex-shrink-0">
                  {showAvatar && (
                    <img
                      src={message?.senderAvatar}
                      alt={message?.senderAvatarAlt}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  )}
                </div>
              )}
              <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[70%]`}>
                {showAvatar && !isMe && (
                  <span className="text-xs text-muted-foreground mb-1 px-2">
                    {message?.senderName}
                  </span>
                )}
                <div
                  className={`px-4 py-2 rounded-lg ${
                    isMe
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message?.content}</p>
                </div>
                <div className="flex items-center gap-2 mt-1 px-2">
                  <span className="text-xs text-muted-foreground">
                    {formatTime(message?.timestamp)}
                  </span>
                  {isMe && (
                    <span className="text-xs">
                      {message?.status === 'sent' && <Icon name="Check" size={12} className="text-muted-foreground" />}
                      {message?.status === 'delivered' && <Icon name="CheckCheck" size={12} className="text-muted-foreground" />}
                      {message?.status === 'read' && <Icon name="CheckCheck" size={12} className="text-primary" />}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div className="flex items-end gap-2">
            <div className="w-8 h-8 flex-shrink-0">
              <img
                src={conversation?.avatar}
                alt={conversation?.avatarAlt}
                className="w-8 h-8 rounded-full object-cover"
              />
            </div>
            <div className="bg-muted px-4 py-3 rounded-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-end gap-2">
          <Button variant="ghost" size="icon" iconName="Paperclip" />
          <Button variant="ghost" size="icon" iconName="Image" />
          <Button variant="ghost" size="icon" iconName="Smile" />
          <div className="flex-1">
            <textarea
              value={messageInput}
              onChange={(e) => setMessageInput(e?.target?.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
          </div>
          <Button
            iconName="Send"
            onClick={handleSend}
            disabled={!messageInput?.trim()}
          >
            Send
          </Button>
        </div>
      </div>
    </>
  );
};

export default ConversationArea;